/**
 * @module app/edition
 * @description Chooses the active edition (Gala night or year-round
 * Campaign). Highest priority first:
 *
 * 1. `?edition=gala` / `?edition=campaign` in the page URL — a shareable link
 *    to either edition from the same deploy (for review, or a one-off event).
 * 2. The Admin → Settings choice saved on this device.
 * 3. `VITE_AFJ_EDITION` at build time — what a deploy ships with. This is the
 *    rollback lever: rebuild with `VITE_AFJ_EDITION=gala` and redeploy.
 * 4. DEFAULT_EDITION_ID in config/editions.ts.
 */
import { DEFAULT_EDITION_ID, EDITIONS, isEditionId, type EditionConfig } from "../config/editions";
import { useSettings } from "../data";
import type { EditionId } from "../domain/types";

/** Pure resolver, so the priority order is testable without a browser. */
export function resolveEditionId(urlValue: string | null, deviceOverride: EditionId | null, buildValue: unknown): EditionId {
    if (isEditionId(urlValue)) return urlValue;
    if (deviceOverride) return deviceOverride;
    if (isEditionId(buildValue)) return buildValue;
    return DEFAULT_EDITION_ID;
}

/** The URL parameter is read once per page load; it does not change while the app runs. */
const URL_EDITION: string | null = new URLSearchParams(window.location.search).get("edition");

/** Where the active edition came from, for the Admin settings panel. */
export type EditionSource = "url" | "device" | "build" | "default";

export function editionSource(deviceOverride: EditionId | null): EditionSource {
    if (isEditionId(URL_EDITION)) return "url";
    if (deviceOverride) return "device";
    if (isEditionId(import.meta.env.VITE_AFJ_EDITION)) return "build";
    return "default";
}

/** Active edition config; re-renders when the Admin setting changes. */
export function useEdition(): EditionConfig {
    const { editionOverride } = useSettings();
    return EDITIONS[resolveEditionId(URL_EDITION, editionOverride, import.meta.env.VITE_AFJ_EDITION)];
}
